import { Ref, forwardRef } from "react"
import { DateTime } from "luxon"
import * as yup from "yup"
import { Form, Formik, FormikProps } from "formik"
import { CreateEditMessageFormValues, MessageAdditionalDataChannelsType, MessageItem } from "../messagesTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { CreateUpdateMessageFormContent, MESSAGE_BODY_LIMIT } from "./CreateUpdateMessageFormContent"
import { MessageTagOption } from "./MessageTagAutoComplete"
import { useDateTime } from "../../../../localization/useDateTime"

export type CreateUpdateMessageFormMode = "create" | "edit"
export type CreateUpdateMessageFormLocation = "dialog" | "page"

type CreateUpdateMessageFormProps = Readonly<{
    mode: CreateUpdateMessageFormMode
    location?: CreateUpdateMessageFormLocation
    initialValues?: CreateEditMessageFormValues | null
    onSubmit: (formData: MessageItem) => void
}>
type FormikRef = Ref<FormikProps<CreateEditMessageFormValues>>

export const CreateUpdateMessageForm = forwardRef(
    ({ mode, initialValues, location = "page", onSubmit }: CreateUpdateMessageFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const dateTimeFactory = useDateTime()
        const today = dateTimeFactory.now()

        const defaultInitialValues = {
            title: "",
            body: "",
            publishTime: today,
            channels: [MessageAdditionalDataChannelsType.IN_APP],
            tag: null,
        }

        const commonSchemaFields = {
            title: yup.string().required(l10n.getString("create-or-edit-message-field-title-required")),
            body: yup
                .string()
                .required(l10n.getString("create-or-edit-message-field-body-required"))
                .test(
                    "is-too-long",
                    l10n.getString("create-or-edit-message-field-body-too-long", {
                        charsLimit: MESSAGE_BODY_LIMIT,
                    }),
                    (value) => (value?.length ?? 0) <= MESSAGE_BODY_LIMIT,
                ),
            publishTime: yup.mixed<DateTime>().nullable(),
        }

        const createSchema = yup.object({
            ...commonSchemaFields,
            channels: yup
                .array()
                .of(yup.mixed<MessageAdditionalDataChannelsType>())
                .min(1, l10n.getString("create-or-edit-message-field-channels-required"))
                .required(l10n.getString("create-or-edit-message-field-channels-required")),
            tag: yup.mixed<MessageTagOption>().required(l10n.getString("create-or-edit-message-field-tag-required")),
        })
        const editSchema = yup.object({
            ...commonSchemaFields,
        })

        const validationSchema = mode === "create" ? createSchema : editSchema

        function handleSubmit(values: CreateEditMessageFormValues) {
            const base: MessageItem = {
                title: values.title,
                body: values.body,
                publishTime: values.publishTime ? dateTimeFactory.toApiISOMinutesFormat(values.publishTime) : null,
            }

            const tagOption = values.tag?.value

            const tagPayload = tagOption
                ? {
                      key: tagOption,
                      displayName: l10n.getStringForEnum("create-or-edit-message-field-tag-mode", tagOption, tagOption),
                  }
                : null

            const modifyMessage =
                mode === "create"
                    ? {
                          ...base,
                          channels: values.channels,
                          ...(tagPayload && { tag: tagPayload }),
                      }
                    : base
            onSubmit(modifyMessage)
        }

        return (
            <>
                <Formik<CreateEditMessageFormValues>
                    enableReinitialize
                    initialValues={initialValues ?? defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                >
                    <Form>
                        <CreateUpdateMessageFormContent location={location} mode={mode} />
                    </Form>
                </Formik>
            </>
        )
    },
)
